import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { Box, Drawer, Fade } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view';
import { useState } from 'react';
import { HelpButton } from './HelpButton';
import { ModuleButton } from './ModuleButton';
import { NotifButton } from './NotifButton';
import { OrgIcon } from './OrgIcon';
import { ProfileButton } from './profile/ProfileButton';
import { SideNavItem } from './SideNavItem';

export const SIDE_RAIL_WIDTH = 88;
export const DRAWER_WIDTH = 240;

export const SideNav = ({ children }) => {

  const module = useModuleFromRoute();
  const { hasRole, org } = usePrincipal();

  // require.context runs at build time to build the SideNav JSON
const sidenavContext = import.meta.glob('/src/**/*.sidenav.js', { eager: true });
  const rawSidenavMenus = Object.keys(sidenavContext)
    .filter(key => {
      if (!key.includes('/org/')) return true;
      // This is the single chokepoint to remove any sideNavs that are org-specific and not part of this org
      return key.includes(`/${org.toLowerCase()}/`);
    })
    .sort((a, b) => {
      // This sorts org-specific routes to come first, guaranteeing that they will be picked over shared routes
      return a.includes('/org/') - b.includes('/org/');
    })
    .map(key => {
      const module = sidenavContext[key];
      return module.default;
    });

  const moduleSidenavMenus = mergeSidenavMenus(rawSidenavMenus);

  moduleSidenavMenus.sort((a, b) => (a.precedence || 0) - (b.precedence || 0));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeSidenavMenu, setActiveSidenavMenu] = useState(null);
  
  const toggleDrawerOpen = () => {
    setDrawerOpen(prev => !prev);
  };

  return (
    <Box sx={theme => ({
      display: 'flex', backgroundColor: theme.palette.surface.light,
    })}>
      {/* Side Rail */}
      <Box
        sx={theme => ({
          width: SIDE_RAIL_WIDTH,
          minWidth: SIDE_RAIL_WIDTH,
          bgcolor: theme.palette.surface.main,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          zIndex: 1300,
          paddingTop: '2px',
          borderRight: '1px solid',
          borderColor: 'divider'
        })}
      >
        {/* Top: Always visible (Dashboard/OrgIcon) */}
        <Box sx={{ pt: 1 }}>
          {/* Organization Icon */}
          <OrgIcon />
        </Box>

        {/* Middle: Scrollable module buttons */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {/* Module Buttons */}
          {moduleSidenavMenus.map((menu) => (
            hasRole(menu.role) &&
            <ModuleButton
              key={menu.id}
              menu={menu}
              isActive={module === menu.id}
              onClick={toggleDrawerOpen}
              setActiveSidenavMenu={setActiveSidenavMenu}
            />
          ))}
        </Box>

        {/* Bottom: Always visible buttons */}
        <Box sx={{ pb: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Placeholder for options */}
          <HelpButton />
          <NotifButton />
          <ProfileButton />
        </Box>
      </Box>

      {/* Drawer for Module Details */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={drawerOpen}
        slotProps={{
          paper: {
            sx: (theme) => ({
              width: DRAWER_WIDTH,
              left: SIDE_RAIL_WIDTH, // offset the drawer so it doesn’t cover the side rail
              bgcolor: theme.palette.surface.main,
              borderTopRightRadius: '12px',
              borderBottomRightRadius: '12px',
              boxShadow: 2,
            }),
          }
        }}
      >
        {activeSidenavMenu && (
          <Fade in={!!activeSidenavMenu} key={activeSidenavMenu.id} timeout={500}>
            <Box sx={{ p: 2 }} data-test-id="sidenav-drawer">
              <Box sx={{ mb: 2, fontWeight: 'bold' }}>
                {activeSidenavMenu.displayName}
              </Box>
              <SimpleTreeView>
                {activeSidenavMenu.children && activeSidenavMenu.children.map(child =>
                  <SideNavItem key={child.id} node={child} />
                )}
              </SimpleTreeView>
            </Box>
          </Fade>
        )}
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={theme => ({
          flexGrow: 1,
          p: 2,
          ml: `${drawerOpen && activeSidenavMenu ? DRAWER_WIDTH : 0}px`,
          transition: 'margin 0.3s ease',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto'
        })}
      >
        {children}
      </Box>
    </Box>
  );
};

/**
 * Helper function to merge org-specific sideNavMenus into the shared sideNavMenu
 * 
 * If two sideNavMenus match on 'id', they will be coalesced. There is special treatment of the 'children'
 * property as both arrays will be combined, and the children will also be coalesced if they match on 'id'
 */
const mergeSidenavMenus = (sideNavMenus) => {
  /** merge two nodes that share the same id */
  const mergeNode = (first, second) => {
    const merged = { ...first, ...second };          // second wins on scalar keys

    // merge children (if either side has them)
    if (first.children || second.children) {
      const combined = [...(first.children || []), ...(second.children || [])];
      const byId = combined.reduce((map, child) => {
        map[child.id] = map[child.id]         // already seen → merge
          ? mergeNode(map[child.id], child)
          : child;                            // first time → take as is
        return map;
      }, {});
      merged.children = Object.values(byId)
        .sort((a, b) => (a.precedence || 0) - (b.precedence || 0));
    }

    return merged;
  };

  // top‑level coalesce
  const byId = sideNavMenus.reduce((map, nav) => {
    map[nav.id] = map[nav.id] ? mergeNode(map[nav.id], nav) : nav;
    return map;
  }, {});

  return Object
    .values(byId)
    .sort((a, b) => (a.precedence || 0) - (b.precedence || 0));
};